/**
 * Story timing shared by the 3D stations and the DOM step list, so the highlighted step in
 * the overlay and the animation in the world always agree.
 *
 * A story is a sequence of steps with individual durations, followed by a hold on the final
 * state. It loops while the slide stays active.
 */
export interface Story {
  /** Seconds per step. */
  durations: number[];
  /** Seconds the final state is held before the story loops. */
  hold: number;
}

export interface StoryState {
  /** Active step, or -1 before the story starts (camera still travelling). */
  step: number;
  /** 0…1 progress inside the active step. */
  local: number;
  /** Seconds since the start of the current cycle (negative before the first cycle). */
  time: number;
  /** 0…1 progress through the whole cycle, hold included. */
  progress: number;
  /** Cycle length in seconds. */
  length: number;
}

export function storyLength(story: Story) {
  return story.durations.reduce((a, b) => a + b, 0) + story.hold;
}

/** Start time (seconds) of each step inside a cycle. */
export function stepStarts(story: Story) {
  const starts: number[] = [];
  let acc = 0;
  for (const d of story.durations) {
    starts.push(acc);
    acc += d;
  }
  return starts;
}

export function storyAt(t: number, story: Story, loop = true): StoryState {
  const length = storyLength(story);
  if (t < 0) return { step: -1, local: 0, time: t, progress: 0, length };
  const time = loop ? t % length : Math.min(t, length);
  let acc = 0;
  for (let i = 0; i < story.durations.length; i++) {
    const d = story.durations[i];
    if (time < acc + d) {
      return { step: i, local: (time - acc) / d, time, progress: time / length, length };
    }
    acc += d;
  }
  return {
    step: story.durations.length - 1,
    local: 1,
    time,
    progress: time / length,
    length,
  };
}

/** Composed "everything has happened" time used under reduced motion. */
export function storyFinalTime(story: Story) {
  return story.durations.reduce((a, b) => a + b, 0) + Math.min(0.5, story.hold / 2);
}

/** Evenly timed story helper. */
export function evenStory(steps: number, seconds: number, hold = 3): Story {
  return { durations: Array.from({ length: steps }, () => seconds), hold };
}
